'use client';

import { type FC, useMemo } from 'react';
import { cn } from '../../../utils/cn';
import type { FighterInit, RoundEvent, Stance, Team } from '@cs-shot/battle-sim';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { WEAPON_ICON_SRC, GRENADE_ICON_SRC, KNIFE_KILL_ICON_SRC, HEADSHOT_ICON_SRC, SMOKE_ICON_SRC, weaponKeyOf } from '../spriteLayout';
import { AtlasIcon } from './shared/AtlasIcon';
import { FaSkull, FaPlus, FaBolt, FaCrosshairs, FaShieldAlt } from '../../icons';
import translations from './RushMid.translations';

const TEAM_COLOR: Record<Team, string> = { t: 'text-gold', ct: 'text-accent-blue' };

/** Мини-иконка стойки в строке лога — цвета как у орбов ActionMenu (красный/золото/синий). */
const StanceChip: FC<{ stance: Stance }> = ({ stance }) => {
  const Icon = stance === 'attack' ? FaCrosshairs : stance === 'defense' ? FaShieldAlt : FaBolt;
  const color = stance === 'attack' ? 'text-accent-red' : stance === 'defense' ? 'text-accent-blue' : 'text-gold';
  return <Icon className={cn('mx-0.5 inline align-middle', color)} size={11} />;
};

type Death = Extract<RoundEvent, { type: 'death' }>;
/** Merge a lethal shot/grenade with the death it caused: returns the death to
 *  append onto each offensive event index, and the death indices to hide. */
function mergeKills(events: RoundEvent[]) {
  const killAt = new Map<number, Death>();
  const hideDeath = new Set<number>();
  const used = new Set<number>();
  events.forEach((ev, di) => {
    if (ev.type !== 'death' || !ev.by) return;
    for (let j = di - 1; j >= 0; j--) {
      const e = events[j];
      if (e.type !== 'grenade' && e.type !== 'knife' && !(e.type === 'shot' && e.hit)) continue;
      if (!used.has(j) && e.shooter === ev.by && e.target === ev.id) {
        killAt.set(j, ev); hideDeath.add(di); used.add(j); break;
      }
    }
  });
  return { killAt, hideDeath };
}

/** Text log of a recorded match, oldest events first. Mostly symbolic so it reads in any language. */
export const MatchLog: FC<{ inits: FighterInit[]; rounds: RoundEvent[][] }> = ({ inits, rounds }) => {
  const { t } = useLocalTranslation(translations);
  const byId = useMemo(() => Object.fromEntries(inits.map((i) => [i.id, i])), [inits]);
  const Name: FC<{ id: string }> = ({ id }) => {
    const f = byId[id];
    return <span className={cn('font-semibold', f ? TEAM_COLOR[f.team] : 'text-label-primary')}>{f?.name ?? id}</span>;
  };
  // loadout is fixed per match, so read the weapon off the shooter's init
  const Weapon: FC<{ id: string }> = ({ id }) => {
    const f = byId[id];
    if (!f) return null;
    return <AtlasIcon src={WEAPON_ICON_SRC[weaponKeyOf(f.team, f.weapon)]} size={28} className="mx-1 align-middle" />;
  };
  const Grenade: FC = () => (
    <AtlasIcon src={GRENADE_ICON_SRC} size={16} className="mx-1 align-middle" />
  );
  // same CS killfeed glyphs the live kill feed uses
  const Knife: FC = () => (
    <AtlasIcon src={KNIFE_KILL_ICON_SRC} size={28} className="mx-1 align-middle" />
  );
  const Headshot: FC = () => (
    <AtlasIcon src={HEADSHOT_ICON_SRC} size={16} className="ml-1 align-middle" />
  );

  return (
    <div className="flex flex-col gap-4">
      {rounds.map((events, r) => (
        <div key={r}>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-label-secondary">
            {t('roundN', { round: r + 1 })}
          </div>
          <div className="flex flex-col gap-1">
            {(() => { const { killAt, hideDeath } = mergeKills(events); return events.map((ev, i) => {
              if (ev.type === 'death' && hideDeath.has(i)) return null; // merged into its lethal hit
              const kill = killAt.get(i); // this offensive event was the killing blow
              const skull = kill ? <><FaSkull className="ml-1 inline text-accent-red" size={13} />{kill.headshot && <Headshot />}</> : null;
              return (
              <div key={i} className="rounded-lg bg-bg-secondary px-3 py-1.5 text-sm text-label-primary">
                {ev.type === 'shot' && (
                  ev.hit ? (
                    <span className="inline-flex flex-wrap items-center">
                      <Name id={ev.shooter} /><StanceChip stance={ev.stance} /><Weapon id={ev.shooter} />
                      <Name id={ev.target} />{ev.targetStance && <StanceChip stance={ev.targetStance} />}
                      {ev.screened && <AtlasIcon src={SMOKE_ICON_SRC} size={14} className="mx-0.5 align-middle" />}
                      {/* синяя цифра = удар гашён focus-resistance — зеркалит синюю цифру в бою */}
                      <span className={ev.shield ? 'text-accent-blue' : 'text-accent-red'}> −{ev.damage}</span>
                      {ev.crit && <span className="text-gold"> ✦</span>}
                      {!!ev.absorbed && <span className="text-gold"> ⚡−{ev.absorbed}</span>}
                      {skull}
                      {!!ev.reflect && (
                        <span className="text-label-secondary"> · {t('logReflect')} <span className="text-accent-red">−{ev.reflect}</span></span>
                      )}
                    </span>
                  ) : (
                    <span className="text-label-secondary">
                      <Name id={ev.shooter} /><StanceChip stance={ev.stance} /><Weapon id={ev.shooter} /><Name id={ev.target} />
                      {' · '}{ev.blind ? t('logMissBlind') : ev.targetStance === 'maneuver' ? t('logMissEvade') : t('logMiss')}
                    </span>
                  )
                )}
                {ev.type === 'grenade' && (() => {
                  const abs = ev.hits.reduce((a, h) => a + (h.absorbed ?? 0), 0);
                  return (
                    <span className="inline-flex items-center">
                      <Name id={ev.shooter} /><Grenade /><Name id={ev.target} />{' '}
                      <span className="text-accent-red">−{ev.hits.reduce((a, h) => a + h.damage, 0)}</span>
                      {abs ? <span className="text-gold"> ⚡−{abs}</span> : null}{skull}
                    </span>
                  );
                })()}
                {ev.type === 'heal' && (
                  <span className="inline-flex items-center"><Name id={ev.id} /> <FaPlus className="mx-1 inline text-accent-green" size={11} /> <span className="text-accent-green">+{ev.amount}</span></span>
                )}
                {ev.type === 'shield' && (
                  <span className="inline-flex items-center text-label-secondary"><Name id={ev.id} /> <FaBolt className="mx-1 inline text-gold" size={12} /> {t('logAdrenaline')} <span className="ml-1 text-gold">+{ev.amount}</span></span>
                )}
                {ev.type === 'knife' && (
                  <span className="inline-flex flex-wrap items-center">
                    <Name id={ev.shooter} /><Knife /><Name id={ev.target} /> <span className="text-accent-red">−{ev.damage}</span>
                    {!!ev.absorbed && <span className="text-gold"> ⚡−{ev.absorbed}</span>}
                    {skull}
                    {!!ev.punish && (
                      <span className="text-label-secondary"> · {t('logPunish')} <span className="text-accent-red">−{ev.punish}</span></span>
                    )}
                  </span>
                )}
                {ev.type === 'smoke' && (
                  <span className="inline-flex items-center text-label-secondary"><Name id={ev.shooter} /> · {t('logSmoke')} → <Name id={ev.target} /></span>
                )}
                {ev.type === 'death' && (
                  ev.by ? (
                    <span className="inline-flex items-center"><Name id={ev.by} />{ev.cause === 'knife' ? <Knife /> : ev.cause === 'grenade' ? <Grenade /> : <Weapon id={ev.by} />}<Name id={ev.id} /> <FaSkull className="ml-1 inline text-accent-red" size={13} />{ev.headshot && <Headshot />}</span>
                  ) : (
                    <span className="inline-flex items-center"><Name id={ev.id} /> <FaSkull className="ml-1 inline text-accent-red" size={13} /></span>
                  )
                )}
              </div>
              );
            }); })()}
          </div>
        </div>
      ))}
      <div className="rounded-lg bg-bg-secondary px-3 py-2 text-xs leading-relaxed text-label-secondary">
        <StanceChip stance="attack" />{t('stanceAttack')} &gt; <StanceChip stance="maneuver" />{t('stanceManeuver')} &gt; <StanceChip stance="defense" />{t('stanceDefense')} &gt; <StanceChip stance="attack" />{t('stanceAttack')} · {t('logLegend')}
      </div>
    </div>
  );
};
